import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getKnowledge, getKnowledgeChildren } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

// `short`/`desc` are i18n keys (suffixed to 'knowledge.catShort'/'knowledge.catDesc')
// rather than literal text — CATS is a module-level const so it can't call
// t() itself; catShort()/catDesc() below resolve them at render time.
const CATS = [
  { id: 'defi',       shortKey: 'Defi',       emoji: '🏦', color: '#10b981',
    terms: ['AMM', 'TVL', 'DEX', 'Staking', 'Yield Farming', 'Liquidity Pool', 'Flash Loan', 'Impermanent Loss', 'MEV', 'Lending Protocol'] },
  { id: 'rwa',        shortKey: 'Rwa',        emoji: '🏢', color: '#0ea5e9',
    terms: ['Tokenization', 'Real World Assets', 'Tokenized Treasury', 'Tokenized Bond', 'Real Estate Token', 'Private Credit', 'Commodity Token', 'Asset Backing', 'Custody', 'Off-chain Asset'] },
  { id: 'layer',      shortKey: 'Layer',      emoji: '🔗', color: '#3b82f6',
    terms: ['L1', 'L2', 'ZK-Rollup', 'Optimistic Rollup', 'Bridge', 'Sharding', 'Modular Blockchain', 'Data Availability', 'EVM', 'Rollup'] },
  { id: 'tokenomics', shortKey: 'Tokenomics', emoji: '🪙', color: '#f59e0b',
    terms: ['FDV', 'TGE', 'Vesting', 'Cliff', 'Token Burn', 'Airdrop', 'Circulating Supply', 'Emission', 'Market Cap', 'Token Distribution'] },
  { id: 'market',     shortKey: 'Market',     emoji: '📊', color: '#8b5cf6',
    terms: ['CEX', 'Liquidity', 'Spread', 'Slippage', 'Order Book', 'Market Maker', 'TWAP', 'VWAP'] },
  { id: 'technical',  shortKey: 'Technical',  emoji: '⚙️', color: '#06b6d4',
    terms: ['Smart Contract', 'Oracle', 'Cross-chain', 'Gas', 'Consensus', 'Validator', 'Node', 'RPC', 'ABI', 'Bytecode'] },
  { id: 'governance', shortKey: 'Governance', emoji: '🏛️', color: '#ec4899',
    terms: ['DAO', 'Governance Token', 'Voting Power', 'Quorum', 'Proposal', 'Treasury', 'Multisig', 'Timelock'] },
  { id: 'security',   shortKey: 'Security',   emoji: '🔐', color: '#ef4444',
    terms: ['Audit', 'Rug Pull', 'Exploit', 'Bug Bounty', 'Phishing', 'Seed Phrase', 'Hardware Wallet', 'Flash Loan Attack'] },
  { id: 'trading',    shortKey: 'Trading',    emoji: '📈', color: '#14b8a6',
    terms: ['Spot Trading', 'Futures', 'Leverage', 'Liquidation', 'Stop Loss', 'Take Profit', 'Portfolio', 'Risk/Reward'] },
];
const catShort = (t, cat) => t(`knowledge.catShort${cat.shortKey}`);
const catDesc = (t, cat) => t(`knowledge.catDesc${cat.shortKey}`);

const W        = 1200;
const MAX_DEPTH = 8;
const MAX_COLS  = 5;
const TERM_SX   = 108;
const TERM_SY   = 88;
const CAT_R     = 42;
const TERM_R    = 27;
const ROOT_R    = 52;
const LAYER_Y   = [72, 238, 418, 598, 778, 958, 1138, 1318, 1498];

function catX(i) { const m = 75; return m + ((W - m * 2) / (CATS.length - 1)) * i; }

function nodePositions(parentX, count) {
  const cols   = Math.min(count, MAX_COLS);
  const totalW = (cols - 1) * TERM_SX;
  let startX   = parentX - totalW / 2;
  const m = 56;
  startX = Math.max(m, Math.min(W - m - totalW, startX));
  return Array.from({ length: count }, (_, j) => ({ x: startX + (j % cols) * TERM_SX, row: Math.floor(j / cols) }));
}

function termPositions(catIdx, count) {
  return nodePositions(catX(catIdx), count).map(p => ({ x: p.x, y: LAYER_Y[2] + p.row * TERM_SY }));
}

function suggPositions(parentX, count, layerY) {
  return nodePositions(parentX, count).map(p => ({ x: p.x, y: layerY + p.row * TERM_SY }));
}

function bez(x1, y1, x2, y2) {
  const my = (y1 + y2) / 2;
  return `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`;
}

// ── Main component ──────────────────────────────────────────────────────────
export default function KnowledgePage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState('map'); // 'map' | 'panel'
  const [activeCat, setActiveCat] = useState(null);
  const [catLoaded, setCatLoaded] = useState({});
  const [defCache,  setDefCache]  = useState({});
  const [fetching,  setFetching]  = useState({});
  // drill: [{term, depth, parentTerm, catId, x, y, ancestors?}]
  // Last element = currently focused node
  const [drill, setDrill] = useState([]);

  // ── Pan / Zoom ────────────────────────────────────────────────────────────
  const [vp, setVp] = useState({ zoom: 1.5, panX: 0, panY: 0 });
  const canvasRef   = useRef(null);
  const dragging    = useRef(false);
  const lastMouse   = useRef({ x: 0, y: 0 });

  // Wheel → zoom centered on cursor
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect   = canvasRef.current.getBoundingClientRect();
    const mx     = e.clientX - rect.left;
    const my     = e.clientY - rect.top;
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    setVp(v => {
      const newZoom = Math.min(2, Math.max(1, v.zoom * factor));
      return {
        zoom: newZoom,
        panX: mx - (mx - v.panX) * (newZoom / v.zoom),
        panY: my - (my - v.panY) * (newZoom / v.zoom),
      };
    });
  }, []);

  // Left-click drag → pan
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setVp(v => ({ ...v, panX: v.panX + dx, panY: v.panY + dy }));
  }, []);

  const handleMouseUp = useCallback(() => { dragging.current = false; }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const fetchDoc = useCallback(async (term, opts = {}) => {
    const key = term.toLowerCase();
    // Only use cache if it has full data (suggestions field present); partial docs from loadCat lack it
    if (defCache[key]?.suggestions !== undefined) return defCache[key];
    if (fetching[key]) return null;
    setFetching(p => ({ ...p, [key]: true }));
    try {
      const doc = await getKnowledge(term, opts);
      setDefCache(p => ({ ...p, [key]: doc }));
      return doc;
    } catch { return null; }
    finally { setFetching(p => { const n = { ...p }; delete n[key]; return n; }); }
  }, [defCache, fetching]);

  const loadCat = useCallback(async (cat) => {
    if (catLoaded[cat.id]) return;
    setCatLoaded(p => ({ ...p, [cat.id]: true }));
    try {
      const { docs } = await getKnowledgeChildren(cat.id);
      const map = {};
      docs.forEach(d => { map[d.termLower] = d; });
      setDefCache(p => ({ ...p, ...map }));
    } catch { }
  }, [catLoaded]);

  // ── Resume last learning position ────────────────────────────────────────
  // Restore the last category + drill path once on mount, then keep saving it.
  const restoredRef = useRef(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem('tf_knowledge_pos');
      if (raw) {
        const saved = JSON.parse(raw);
        const cat = CATS.find(c => c.id === saved.catId);
        if (cat) {
          setActiveCat(cat);
          loadCat(cat);
          if (Array.isArray(saved.drill) && saved.drill.length) {
            setDrill(saved.drill);
            // Re-fetch definitions along the drill path so panels + child
            // suggestion layers render exactly where the user left off.
            saved.drill.forEach(e => {
              const opts = e.depth === 2
                ? { depth: 2, parentCatId: e.catId }
                : { depth: e.depth, parentTerm: e.parentTerm, ancestors: e.ancestors || [] };
              fetchDoc(e.term, opts);
            });
          }
        }
      }
    } catch { /* corrupt state — start fresh */ }
    restoredRef.current = true;
  }, []);

  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem('tf_knowledge_pos', JSON.stringify({
        catId: activeCat?.id || null,
        drill: drill.map(({ term, depth, parentTerm, catId, x, y, ancestors }) =>
          ({ term, depth, parentTerm, catId, x, y, ancestors })),
      }));
    } catch { /* storage full/blocked — ignore */ }
  }, [activeCat, drill]);

  // ── Computed values ───────────────────────────────────────────────────────
  const activeCatIdx = activeCat ? CATS.indexOf(activeCat) : -1;
  const visibleTerms = activeCat ? activeCat.terms.slice(0, MAX_COLS * 2) : [];
  const termPos      = activeCat ? termPositions(activeCatIdx, visibleTerms.length) : [];

  const focusedEntry = drill.length > 0 ? drill[drill.length - 1] : null;
  const parentEntry  = drill.length > 1 ? drill[drill.length - 2] : null;
  const isFocused    = drill.length > 0;

  // Auto-pan to show focused node + children when drill changes
  // (placed here so focusedEntry is already declared above — avoids TDZ in production builds)
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvasH = canvasRef.current.getBoundingClientRect().height;
    if (!focusedEntry) {
      setVp(v => ({ ...v, panY: 0 }));
      return;
    }
    const fd = focusedEntry.depth;
    const childBottomY = (LAYER_Y[fd + 1] !== undefined ? LAYER_Y[fd + 1] : (focusedEntry.y + 200)) + TERM_R + 80;
    setVp(v => {
      const childScreenY = childBottomY * v.zoom + v.panY;
      if (childScreenY > canvasH) {
        const targetPanY = canvasH - childBottomY * v.zoom;
        return { ...v, panY: Math.min(0, targetPanY) };
      }
      return v;
    });
  }, [focusedEntry]);

  // Children of focused node (filtered, same dedup logic)
  const focusedDoc = focusedEntry ? defCache[focusedEntry.term.toLowerCase()] : null;
  const ancestorSetFull = new Set([
    ...drill.map(e => e.term.toLowerCase()),
    ...visibleTerms.map(t => t.toLowerCase()),
  ]);
  const focusedChildren = (focusedDoc?.suggestions || []).filter(
    s => !ancestorSetFull.has(s.toLowerCase())
  );

  // ── Visibility helpers ────────────────────────────────────────────────────
  // Returns true if node should be rendered (false = completely skip)
  const isTermVisible = (term) => {
    if (!isFocused) return true;
    const t = term.toLowerCase();
    // Show the term only if it's part of the ancestor chain (depth-2 node in drill)
    return drill.some(e => e.depth === 2 && e.term.toLowerCase() === t);
  };

  // Categories (tầng 2) are always visible
  const isCatVisible = () => true;

  // ── Click handlers ────────────────────────────────────────────────────────
  const handleCatClick = (cat) => {
    // If focused on a depth-2 term, clicking the active cat = go back to full term view
    if (isFocused && activeCat?.id === cat.id && focusedEntry?.depth === 2) {
      setDrill([]);
      return;
    }
    if (isFocused && activeCat?.id === cat.id) {
      // Clicking cat while focused deeper → clear drill
      setDrill([]);
      return;
    }
    if (activeCat?.id === cat.id && !isFocused) {
      setActiveCat(null); setDrill([]);
    } else {
      setActiveCat(cat); setDrill([]);
      loadCat(cat);
    }
  };

  const handleTermClick = async (term, catId, x, y) => {
    // Clicking the direct parent → go back one level
    if (parentEntry?.term === term) { setDrill(d => d.slice(0, -1)); return; }
    // Clicking focused term again → unfocus
    if (focusedEntry?.term === term && focusedEntry?.depth === 2) { setDrill([]); return; }
    setDrill([{ term, depth: 2, parentTerm: null, catId, x, y }]);
    await fetchDoc(term, { depth: 2, parentCatId: catId });
  };

  const handleSuggClick = async (term, depth, parentTerm, x, y, drillIdx) => {
    // Clicking the direct parent → go back one level
    if (parentEntry?.term === term) { setDrill(d => d.slice(0, -1)); return; }
    // Clicking focused term → unfocus one level
    if (focusedEntry?.term === term) { setDrill(d => d.slice(0, -1)); return; }
    const ancestors = drill.slice(0, drillIdx + 1).map(e => e.term);
    setDrill(d => [
      ...d.slice(0, drillIdx + 1),
      { term, depth, parentTerm, catId: null, x, y, ancestors },
    ]);
    await fetchDoc(term, { depth, parentTerm, ancestors });
  };

  // ── Build suggestion layers ───────────────────────────────────────────────
  const suggLayers = [];
  for (let i = 0; i < drill.length; i++) {
    const entry = drill[i];
    const doc   = defCache[entry.term.toLowerCase()];
    if (!doc?.suggestions?.length) break;
    const nextDepth = Math.min(MAX_DEPTH, entry.depth + 1);
    if (nextDepth > MAX_DEPTH) break;

    const ancestorSet = new Set(drill.slice(0, i + 1).map(e => e.term.toLowerCase()));
    visibleTerms.forEach(t => ancestorSet.add(t.toLowerCase()));
    const filtered = doc.suggestions.filter(s => !ancestorSet.has(s.toLowerCase()));
    if (!filtered.length) break;

    const layerY    = LAYER_Y[3 + i];
    const positions = suggPositions(entry.x, filtered.length, layerY);
    const selectedTerm = drill[i + 1]?.term || null;
    suggLayers.push({ drillIdx: i, depth: nextDepth, parentTerm: entry.term, suggestions: filtered, positions, parentX: entry.x, layerY, selectedTerm });
  }

  // ── SVG dimensions & viewBox ──────────────────────────────────────────────
  let svgFullH = LAYER_Y[1] + CAT_R + 60;
  if (activeCat) {
    const rows = Math.ceil(visibleTerms.length / MAX_COLS);
    svgFullH = LAYER_Y[2] + (rows - 1) * TERM_SY + TERM_R + 60;
  }
  if (suggLayers.length > 0) {
    const last = suggLayers[suggLayers.length - 1];
    const rows = Math.ceil(last.suggestions.length / MAX_COLS);
    svgFullH = last.layerY + (rows - 1) * TERM_SY + TERM_R + 60;
  }

  let viewBox     = `0 0 ${W} ${svgFullH}`;
  let svgDisplayH = svgFullH;

  // No viewBox zoom when focused — tầng 1 and 2 remain visible at all times

  // ── Panel data ────────────────────────────────────────────────────────────
  const panelDoc = focusedEntry ? defCache[focusedEntry.term.toLowerCase()] : null;

  // Auto-switch to panel on mobile when a term is focused
  useEffect(() => {
    if (isMobile && focusedEntry) setMobileView('panel');
  }, [isMobile, focusedEntry]);

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)', background: 'var(--bg)' }}>

        {/* Category chips — horizontal scroll row */}
        <div style={{ flexShrink: 0, overflowX: 'auto', display: 'flex', gap: 8, padding: '10px 14px',
          background: 'var(--surface)', borderBottom: '1px solid var(--border)', WebkitOverflowScrolling: 'touch' }}>
          {CATS.map(cat => {
            const active = activeCat?.id === cat.id;
            return (
              <button key={cat.id}
                onClick={() => { const exp = activeCat?.id === cat.id; setActiveCat(exp ? null : cat); setDrill([]); if (!exp) loadCat(cat); }}
                style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                  borderRadius: 20, border: `1.5px solid ${cat.color}`,
                  background: active ? cat.color : 'transparent',
                  color: active ? '#fff' : cat.color,
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {cat.emoji} {catShort(t, cat)}
              </button>
            );
          })}
        </div>

        {/* Back nav when drilling down */}
        {(focusedEntry || (activeCat && drill.length === 0)) && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px',
            background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <button
              onClick={() => focusedEntry ? setDrill(d => d.slice(0, -1)) : setActiveCat(null)}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 13, padding: 0, fontWeight: 600 }}>
              {t('knowledge.backLink')}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {focusedEntry ? focusedEntry.term : activeCat?.name}
            </span>
          </div>
        )}

        {/* Definition panel — full height */}
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--surface)', padding: '16px 18px' }}>
          {!activeCat && !focusedEntry && (
            <EmptyPanel onCatClick={(cat) => { setActiveCat(cat); setDrill([]); loadCat(cat); }} />
          )}
          {activeCat && !focusedEntry && (
            <CategoryPanel cat={activeCat}
              loadedCount={activeCat.terms.filter(t => defCache[t.toLowerCase()]).length}
              onTermClick={(term) => {
                const j = visibleTerms.indexOf(term);
                const pos = termPos[j] || { x: catX(activeCatIdx), y: LAYER_Y[2] };
                handleTermClick(term, activeCat.id, pos.x, pos.y);
              }}/>
          )}
          {focusedEntry && (
            <TermPanel term={focusedEntry.term} depth={focusedEntry.depth} doc={panelDoc}
              loading={!!fetching[focusedEntry.term.toLowerCase()]} cat={activeCat}
              ancestorSet={ancestorSetFull}
              onSuggestionDrill={(sug) => {
                const fd = focusedEntry.depth;
                const childLayerY = LAYER_Y[fd + 1] || (focusedEntry.y + 200);
                const childPositions = suggPositions(focusedEntry.x, focusedChildren.length, childLayerY);
                const idx = focusedChildren.indexOf(sug);
                const pos = childPositions[idx] || { x: focusedEntry.x, y: childLayerY };
                handleSuggClick(sug, Math.min(MAX_DEPTH, fd + 1), focusedEntry.term, pos.x, pos.y, drill.length - 1);
              }}
              onRelatedClick={(rel) => {
                const relCat = CATS.find(c => c.terms.some(t => t.toLowerCase() === rel.toLowerCase()));
                if (relCat) { setActiveCat(relCat); setDrill([]); loadCat(relCat); }
              }}
              onBack={() => setDrill(d => d.slice(0, -1))}
            />
          )}
        </div>
        <style>{`
          @keyframes pulse { 0%,100% { opacity:0.35; transform:scale(1); } 50% { opacity:0.75; transform:scale(1.1); } }
          @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div style={s.page}>
        {/* ── Left: Tower SVG ─────────────────────────────────────── */}
        <div style={s.mapWrap}>
          <div style={s.mapHeader}>
            <h2 style={s.mapTitle}>{t('knowledge.pageTitle')}</h2>

            {/* Breadcrumb path */}
            {isFocused ? (
              <div style={s.breadcrumb}>
                <span style={s.bcCat}
                  onClick={() => setDrill([])}
                  title={t('knowledge.backToCategory')}>
                  {activeCat?.emoji} {activeCat ? catShort(t, activeCat) : ''}
                </span>
                {drill.map((e, i) => (
                  <React.Fragment key={e.term}>
                    <span style={s.bcSep}>›</span>
                    <span
                      style={{ ...s.bcItem, color: i === drill.length - 1 ? (activeCat?.color || 'var(--accent)') : 'var(--text-muted)' }}
                      onClick={() => setDrill(d => d.slice(0, i + 1))}
                    >{e.term}</span>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div style={s.mapHint}>{t('knowledge.mapHint')}</div>
            )}

            {isFocused && (
              <button style={s.resetBtn} onClick={() => setDrill([])}>{t('knowledge.collapse')}</button>
            )}
          </div>

          <div
            ref={canvasRef}
            style={s.canvas}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onContextMenu={e => e.preventDefault()}
          >
            {/* Zoom controls */}
            <div style={s.zoomControls}>
              <button style={s.zoomBtn} onClick={() => setVp(v => ({ ...v, zoom: Math.min(2, +(v.zoom * 1.2).toFixed(2)) }))}>＋</button>
              <span style={s.zoomLabel}>{Math.round(vp.zoom * 100)}%</span>
              <button style={s.zoomBtn} onClick={() => setVp(v => ({ ...v, zoom: Math.max(1, +(v.zoom / 1.2).toFixed(2)) }))}>－</button>
              <button style={{ ...s.zoomBtn, fontSize: 10 }} onClick={() => setVp({ zoom: 1.5, panX: 0, panY: 0 })}>↺</button>
            </div>
            <div style={{
              transform: `translate(${vp.panX}px, ${vp.panY}px) scale(${vp.zoom})`,
              transformOrigin: '0 0',
              willChange: 'transform',
              display: 'inline-block',
            }}>
            <svg
              viewBox={viewBox}
              preserveAspectRatio="xMidYMid meet"
              style={{ ...s.svg, width: W, height: svgDisplayH }}
            >
              <defs>
                {CATS.map(cat => (
                  <radialGradient key={cat.id} id={`grad-${cat.id}`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={cat.color} stopOpacity="0.95" />
                    <stop offset="100%" stopColor={cat.color} stopOpacity="0.55" />
                  </radialGradient>
                ))}
                <radialGradient id="grad-root" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#1f58a4" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="#29a7df" stopOpacity="0.7" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="glow-sm">
                  <feGaussianBlur stdDeviation="2" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* ── Root → Category edges (always visible) ── */}
              {CATS.map((cat, i) => (
                <path key={`re-${cat.id}`}
                  d={bez(W / 2, LAYER_Y[0] + ROOT_R, catX(i), LAYER_Y[1] - CAT_R)}
                  fill="none"
                  stroke={activeCat?.id === cat.id ? cat.color : '#b4cde8'}
                  strokeWidth={activeCat?.id === cat.id ? 2.5 : 1}
                  strokeOpacity={activeCat?.id === cat.id ? 0.8 : 0.22}
                />
              ))}

              {/* ── Category → Term edges ── */}
              {activeCat && visibleTerms.map((term, j) => {
                if (isFocused && !drill.some(e => e.depth === 2 && e.term === term)) return null;
                const { x: tx, y: ty } = termPos[j];
                return (
                  <path key={`te-${term}`}
                    d={bez(catX(activeCatIdx), LAYER_Y[1] + CAT_R, tx, ty - TERM_R)}
                    fill="none" stroke={activeCat.color}
                    strokeWidth={isFocused ? 2 : 1} strokeOpacity={isFocused ? 0.5 : 0.35} strokeDasharray="5 4"
                  />
                );
              })}

              {/* ── Focused mode edges: full ancestor chain → focused → children ── */}
              {isFocused && focusedEntry && (() => {
                const fd = focusedEntry.depth;
                const fx = focusedEntry.x;
                const fy = focusedEntry.y;
                const color = activeCat?.color || '#64748b';
                // Parent position (direct parent of focusedEntry)
                const parentY = fd === 2
                  ? LAYER_Y[1] + CAT_R
                  : (parentEntry?.y || LAYER_Y[fd - 1]) + TERM_R;
                const parentX = fd === 2 ? catX(activeCatIdx) : (parentEntry?.x || fx);

                // Intermediate drill chain edges: drill[0]→drill[1], drill[1]→drill[2], … up to parentEntry
                const chainEdges = [];
                for (let i = 0; i < drill.length - 2; i++) {
                  const from = drill[i];
                  const to   = drill[i + 1];
                  chainEdges.push(
                    <path key={`ce-${i}`}
                      d={bez(from.x, from.y + TERM_R, to.x, to.y - TERM_R)}
                      fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.4}
                    />
                  );
                }

                return (
                  <>
                    {/* Intermediate ancestor chain edges */}
                    {chainEdges}
                    {/* Direct parent → Focused */}
                    <path
                      d={bez(parentX, parentY, fx, fy - TERM_R)}
                      fill="none" stroke={color} strokeWidth={2} strokeOpacity={0.5}
                    />
                    {/* Focused → each child */}
                    {(() => {
                      const childLayerY = LAYER_Y[fd + 1] || (fy + 200);
                      const childPositions = suggPositions(fx, focusedChildren.length, childLayerY);
                      return focusedChildren.map((_, k) => (
                        <path key={`fce-${k}`}
                          d={bez(fx, fy + TERM_R, childPositions[k].x, childPositions[k].y - TERM_R)}
                          fill="none" stroke={color}
                          strokeWidth={1} strokeOpacity={0.35} strokeDasharray="5 4"
                        />
                      ));
                    })()}
                  </>
                );
              })()}

              {/* ── Unfocused: Term→Suggestion edges ── */}
              {!isFocused && suggLayers[0] && visibleTerms.map((term, j) => {
                if (drill[0]?.term !== term) return null;
                const { x: tx, y: ty } = termPos[j];
                return suggLayers[0].positions.map((p, k) => (
                  <path key={`s0e-${k}`}
                    d={bez(tx, ty + TERM_R, p.x, p.y - TERM_R)}
                    fill="none" stroke={activeCat.color}
                    strokeWidth={1} strokeOpacity={0.3} strokeDasharray="5 4"
                  />
                ));
              })}
              {!isFocused && suggLayers.slice(1).map((layer, li) => {
                const prev   = suggLayers[li];
                const selIdx = prev.suggestions.indexOf(prev.selectedTerm);
                if (selIdx < 0) return null;
                const pp = prev.positions[selIdx];
                return layer.positions.map((p, k) => (
                  <path key={`s${li + 1}e-${k}`}
                    d={bez(pp.x, pp.y + TERM_R, p.x, p.y - TERM_R)}
                    fill="none" stroke={activeCat?.color || '#64748b'}
                    strokeWidth={1} strokeOpacity={0.28} strokeDasharray="5 4"
                  />
                ));
              })}

              {/* ── Term nodes (layer 3) ── */}
              {activeCat && visibleTerms.map((term, j) => {
                if (!isTermVisible(term)) return null;
                const { x: tx, y: ty } = termPos[j];
                const isParent = isFocused && focusedEntry?.depth > 2 && drill[0]?.term === term && !parentEntry;
                const isBack   = isFocused && parentEntry?.term === term;
                const isSel    = drill[0]?.term === term;
                return (
                  <TermNode key={`tn-${term}`}
                    x={tx} y={ty} r={TERM_R}
                    label={term}
                    emoji={defCache[term.toLowerCase()]?.emoji}
                    loading={!!fetching[term.toLowerCase()]}
                    selected={isSel && !isFocused}
                    focused={isFocused && focusedEntry?.term === term}
                    isBack={isBack || (isFocused && focusedEntry?.depth === 2 && isSel)}
                    color={activeCat.color}
                    onClick={() => handleTermClick(term, activeCat.id, tx, ty)}
                  />
                );
              })}

              {/* ── Suggestion nodes (focused mode: show from focusedChildren) ── */}
              {isFocused && focusedEntry && (() => {
                const fd = focusedEntry.depth;
                const childLayerY = LAYER_Y[fd + 1] || (focusedEntry.y + 200);
                const childPositions = suggPositions(focusedEntry.x, focusedChildren.length, childLayerY);
                return focusedChildren.map((sug, j) => {
                  const { x, y } = childPositions[j];
                  const nextDepth = Math.min(MAX_DEPTH, fd + 1);
                  // Find which suggLayer and drillIdx this child belongs to
                  const drillIdx = drill.length - 1;
                  return (
                    <TermNode key={`fc-${sug}`}
                      x={x} y={y} r={TERM_R}
                      label={sug}
                      emoji={defCache[sug.toLowerCase()]?.emoji}
                      loading={!!fetching[sug.toLowerCase()]}
                      selected={false} focused={false} isBack={false}
                      color={activeCat?.color || '#64748b'}
                      onClick={() => handleSuggClick(sug, nextDepth, focusedEntry.term, x, y, drillIdx)}
                    />
                  );
                });
              })()}

              {/* ── Suggestion nodes (unfocused mode) ── */}
              {!isFocused && suggLayers.map((layer, li) =>
                layer.suggestions.map((sug, j) => {
                  const { x, y } = layer.positions[j];
                  return (
                    <TermNode key={`sl${li}-${sug}`}
                      x={x} y={y} r={TERM_R}
                      label={sug}
                      emoji={defCache[sug.toLowerCase()]?.emoji}
                      loading={!!fetching[sug.toLowerCase()]}
                      selected={layer.selectedTerm === sug}
                      focused={false} isBack={false}
                      color={activeCat?.color || '#64748b'}
                      onClick={() => handleSuggClick(sug, layer.depth, layer.parentTerm, x, y, layer.drillIdx)}
                    />
                  );
                })
              )}

              {/* ── Focused node (rendered last so it's on top) ── */}
              {isFocused && focusedEntry && (() => {
                const { x, y, term, depth } = focusedEntry;
                // Check if this is a term (depth 2) — already rendered above; skip re-render
                if (depth === 2) return null;
                // It's a suggestion node
                return (
                  <TermNode
                    x={x} y={y} r={TERM_R}
                    label={term}
                    emoji={defCache[term.toLowerCase()]?.emoji}
                    loading={!!fetching[term.toLowerCase()]}
                    selected={false} focused={true} isBack={false}
                    color={activeCat?.color || '#64748b'}
                    onClick={() => setDrill(d => d.slice(0, -1))}
                  />
                );
              })()}

              {/* ── Intermediate ancestor nodes (drill chain, skipping first term and last two) ── */}
              {isFocused && drill.slice(1, -2).map((entry) => (
                entry.depth > 2 ? (
                  <TermNode key={`anc-${entry.term}`}
                    x={entry.x} y={entry.y} r={TERM_R}
                    label={entry.term}
                    emoji={defCache[entry.term.toLowerCase()]?.emoji}
                    loading={false}
                    selected={false} focused={false} isBack={false}
                    color={activeCat?.color || '#64748b'}
                    onClick={() => {}}
                  />
                ) : null
              ))}

              {/* ── Parent suggestion node (when focused deep) ── */}
              {isFocused && parentEntry && parentEntry.depth > 2 && (() => {
                const { x, y, term } = parentEntry;
                return (
                  <TermNode
                    x={x} y={y} r={TERM_R}
                    label={term}
                    emoji={defCache[term.toLowerCase()]?.emoji}
                    loading={false}
                    selected={false} focused={false} isBack={true}
                    color={activeCat?.color || '#64748b'}
                    onClick={() => setDrill(d => d.slice(0, -1))}
                  />
                );
              })()}

              {/* ── Category nodes ── */}
              {CATS.map((cat, i) => {
                if (!isCatVisible(cat)) return null;
                const x    = catX(i);
                const isExp = activeCat?.id === cat.id;
                const isBackTarget = isFocused && isExp; // clicking cat = go back
                return (
                  <g key={`cn-${cat.id}`} onClick={() => handleCatClick(cat)} style={{ cursor: 'pointer' }}>
                    {isExp && (
                      <circle cx={x} cy={LAYER_Y[1]} r={CAT_R + 11}
                        fill={cat.color} fillOpacity={0.1}
                        stroke={cat.color} strokeWidth={1} strokeOpacity={0.25} />
                    )}
                    <circle cx={x} cy={LAYER_Y[1]} r={CAT_R}
                      fill={isExp ? `url(#grad-${cat.id})` : 'var(--surface)'}
                      stroke={cat.color}
                      strokeWidth={isExp ? 2.5 : 1.5}
                      strokeOpacity={isExp ? 1 : 0.5}
                      filter={isExp ? 'url(#glow)' : undefined}
                    />
                    <text x={x} y={LAYER_Y[1] - 8} textAnchor="middle" fontSize={20} style={{ userSelect: 'none' }}>{cat.emoji}</text>
                    <text x={x} y={LAYER_Y[1] + 13} textAnchor="middle" fontSize={9}
                      fontWeight={700} fill={isExp ? '#fff' : cat.color} style={{ userSelect: 'none' }}>{catShort(t, cat)}</text>
                    {isBackTarget && (
                      <text x={x} y={LAYER_Y[1] + CAT_R + 13} textAnchor="middle"
                        fontSize={8} fill={cat.color} opacity={0.8} style={{ userSelect: 'none' }}>{t('knowledge.backShort')}</text>
                    )}
                    {!isBackTarget && (
                      <text x={x} y={LAYER_Y[1] + CAT_R + 13} textAnchor="middle"
                        fontSize={8.5} fill="var(--text-dim)" style={{ userSelect: 'none' }}>{cat.terms.length} {t('knowledge.termsSuffix')}</text>
                    )}
                  </g>
                );
              })}

              {/* ── Root node (always visible) ── */}
              {(
                <g onClick={() => { setActiveCat(null); setDrill([]); }} style={{ cursor: 'pointer' }}>
                  <circle cx={W / 2} cy={LAYER_Y[0]} r={ROOT_R + 10}
                    fill="#1f58a4" fillOpacity={0.07} stroke="#29a7df" strokeWidth={1} strokeOpacity={0.2} />
                  <circle cx={W / 2} cy={LAYER_Y[0]} r={ROOT_R}
                    fill="url(#grad-root)" stroke="#29a7df" strokeWidth={2} filter="url(#glow)" />
                  <text x={W / 2} y={LAYER_Y[0] - 12} textAnchor="middle" fontSize={24} style={{ userSelect: 'none' }}>🧠</text>
                  <text x={W / 2} y={LAYER_Y[0] + 10} textAnchor="middle" fontSize={11}
                    fontWeight={700} fill="#fff" style={{ userSelect: 'none' }}>{t('knowledge.rootTitle')}</text>
                  <text x={W / 2} y={LAYER_Y[0] + 24} textAnchor="middle" fontSize={9}
                    fill="rgba(255,255,255,0.65)" style={{ userSelect: 'none' }}>{t('knowledge.rootSubtitle')}</text>
                </g>
              )}

              {/* ── Layer labels ── */}
              <LayerLabel y={LAYER_Y[0]} text={t('knowledge.layerN', { n: 1 })} />
              <LayerLabel y={LAYER_Y[1]} text={t('knowledge.layerN', { n: 2 })} />
              {activeCat && !isFocused && <LayerLabel y={LAYER_Y[2]} text={t('knowledge.layerN', { n: 3 })} />}
              {!isFocused && suggLayers.map((layer, li) => (
                <LayerLabel key={`ll-${li}`} y={layer.layerY} text={t('knowledge.layerN', { n: 4 + li })} highlight={li === suggLayers.length - 1} />
              ))}
              {isFocused && focusedEntry && (
                <LayerLabel y={focusedEntry.y} text={t('knowledge.layerN', { n: focusedEntry.depth + 1 })} highlight />
              )}
            </svg>
            </div>{/* end transform wrapper */}
          </div>{/* end canvas */}
        </div>

        {/* ── Right: Detail panel ─────────────────────────────────── */}
        <div style={s.panel}>
          {!activeCat && !focusedEntry && <EmptyPanel />}
          {activeCat && !focusedEntry && (
            <CategoryPanel
              cat={activeCat}
              loadedCount={activeCat.terms.filter(t => defCache[t.toLowerCase()]).length}
              onTermClick={(term) => {
                const j = visibleTerms.indexOf(term);
                const pos = termPos[j] || { x: catX(activeCatIdx), y: LAYER_Y[2] };
                handleTermClick(term, activeCat.id, pos.x, pos.y);
              }}
            />
          )}
          {focusedEntry && (
            <TermPanel
              term={focusedEntry.term}
              depth={focusedEntry.depth}
              doc={panelDoc}
              loading={!!fetching[focusedEntry.term.toLowerCase()]}
              cat={activeCat}
              ancestorSet={ancestorSetFull}
              onSuggestionDrill={(sug) => {
                const fd   = focusedEntry.depth;
                const childLayerY = LAYER_Y[fd + 1] || (focusedEntry.y + 200);
                const childPositions = suggPositions(focusedEntry.x, focusedChildren.length, childLayerY);
                const idx  = focusedChildren.indexOf(sug);
                const pos  = childPositions[idx] || { x: focusedEntry.x, y: childLayerY };
                handleSuggClick(sug, Math.min(MAX_DEPTH, fd + 1), focusedEntry.term, pos.x, pos.y, drill.length - 1);
              }}
              onRelatedClick={(rel) => {
                const relCat = CATS.find(c => c.terms.some(t => t.toLowerCase() === rel.toLowerCase()));
                if (relCat) {
                  setActiveCat(relCat); setDrill([]);
                  loadCat(relCat);
                  const idx = relCat.terms.indexOf(rel);
                  const pos = termPositions(CATS.indexOf(relCat), relCat.terms.length)[idx] || { x: catX(CATS.indexOf(relCat)), y: LAYER_Y[2] };
                  setTimeout(() => handleTermClick(rel, relCat.id, pos.x, pos.y), 50);
                }
              }}
              onBack={() => setDrill(d => d.slice(0, -1))}
            />
          )}
        </div>

        <style>{`
          @keyframes pulse { 0%,100% { opacity:0.35; transform:scale(1); } 50% { opacity:0.75; transform:scale(1.1); } }
          @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes backPulse { 0%,100% { stroke-opacity:0.4; } 50% { stroke-opacity:0.9; } }
        `}</style>
      </div>
    </>
  );
}

// ── TermNode SVG component ──────────────────────────────────────────────────
function TermNode({ x, y, r, label, emoji, loading, selected, focused, isBack, color, onClick }) {
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Focus ring */}
      {focused && (
        <circle cx={x} cy={y} r={r + 12}
          fill={color} fillOpacity={0.12}
          stroke={color} strokeWidth={2} strokeOpacity={0.5}
          style={{ animation: 'pulse 2s infinite' }} />
      )}
      {/* Selected ring (unfocused mode) */}
      {selected && !focused && (
        <circle cx={x} cy={y} r={r + 9}
          fill={color} fillOpacity={0.12}
          stroke={color} strokeWidth={1} strokeOpacity={0.3}
          style={{ animation: 'pulse 2s infinite' }} />
      )}
      {/* Back indicator ring */}
      {isBack && (
        <circle cx={x} cy={y} r={r + 8}
          fill="none"
          stroke={color} strokeWidth={1.5} strokeOpacity={0.6}
          strokeDasharray="4 3"
          style={{ animation: 'backPulse 1.5s ease infinite' }} />
      )}
      <circle cx={x} cy={y} r={r}
        fill={focused ? color : selected ? color : 'var(--surface)'}
        stroke={color}
        strokeWidth={focused ? 2.5 : selected ? 2.5 : 1.5}
        strokeOpacity={focused ? 1 : selected ? 1 : 0.55}
        filter={focused || selected ? 'url(#glow-sm)' : undefined}
      />
      {loading ? (
        <text x={x} y={y + 5} textAnchor="middle" fontSize={14} fill="#6b8dad">⟳</text>
      ) : (
        <text x={x} y={y + 5} textAnchor="middle" fontSize={emoji ? 15 : 10}
          fill={focused || selected ? '#fff' : '#6b8dad'}>
          {emoji || '?'}
        </text>
      )}
      <text x={x} y={y + r + 14} textAnchor="middle" fontSize={9.5}
        fontWeight={focused || selected ? 700 : 400}
        fill={focused ? color : selected ? color : 'var(--text-muted)'}
        style={{ userSelect: 'none' }}>
        {label.length > 13 ? label.slice(0, 12) + '…' : label}
      </text>
      {isBack && (
        <text x={x} y={y - r - 6} textAnchor="middle" fontSize={8}
          fill={color} opacity={0.75} style={{ userSelect: 'none' }}>← back</text>
      )}
    </g>
  );
}

function LayerLabel({ y, text, highlight }) {
  return (
    <text x={14} y={y + 4} fontSize={8.5} fontWeight={700} letterSpacing={0.5}
      fill={highlight ? 'var(--tf-cyan)' : 'var(--text-dim)'}
      style={{ userSelect: 'none' }}>{text}</text>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────
function EmptyPanel({ onCatClick }) {
  const { t } = useTranslation();
  return (
    <div style={s.emptyPanel}>
      <div style={s.emptyIcon}>🗺️</div>
      <div style={s.emptyTitle}>{t('knowledge.emptyPanelTitle')}</div>
      <div style={s.emptyDesc}>
        {t('knowledge.emptyPanelDesc')}
      </div>
      <div style={s.catGrid}>
        {CATS.map(cat => (
          onCatClick
            ? (
              <button key={cat.id}
                onClick={() => onCatClick(cat)}
                style={{ ...s.catChip, borderColor: cat.color + '60', cursor: 'pointer', background: 'none' }}>
                <span>{cat.emoji}</span>
                <span style={{ color: cat.color, fontSize: 11 }}>{catShort(t, cat)}</span>
              </button>
            )
            : (
              <div key={cat.id} style={{ ...s.catChip, borderColor: cat.color + '60' }}>
                <span>{cat.emoji}</span>
                <span style={{ color: cat.color, fontSize: 11 }}>{catShort(t, cat)}</span>
              </div>
            )
        ))}
      </div>
    </div>
  );
}

function CategoryPanel({ cat, loadedCount, onTermClick }) {
  const { t } = useTranslation();
  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      <div style={{ ...s.panelCatHeader, borderColor: cat.color + '40', background: cat.color + '12' }}>
        <span style={s.panelEmoji}>{cat.emoji}</span>
        <div>
          <div style={{ ...s.panelCatName, color: cat.color }}>{catShort(t, cat)}</div>
          <div style={s.panelCatDesc}>{catDesc(t, cat)}</div>
        </div>
      </div>
      <div style={s.panelSection}>
        <div style={s.panelSectionTitle}>
          {t('knowledge.termsCountLoaded', { total: cat.terms.length, loaded: loadedCount })}
        </div>
        <div style={s.termList}>
          {cat.terms.map(term => (
            <button key={term} onClick={() => onTermClick(term)}
              style={{ ...s.termBtn, borderColor: cat.color + '40' }}>
              <span style={{ color: cat.color, fontSize: 11, fontWeight: 700 }}>›</span>
              <span style={s.termBtnText}>{term}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const DEPTH_COLOR = ['', '', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#f97316', '#ef4444', '#dc2626'];
const DEPTH_LABEL_KEY = ['', '', 'depth2', 'depth3', 'depth4', 'depth5', 'depth6', 'depth7', 'depth8'];

function TermPanel({ term, depth, doc, loading, cat, ancestorSet = new Set(), onSuggestionDrill, onRelatedClick, onBack }) {
  const { t } = useTranslation();
  const dColor       = DEPTH_COLOR[depth] || cat?.color || '#64748b';
  const dLabel       = DEPTH_LABEL_KEY[depth] ? t(`knowledge.${DEPTH_LABEL_KEY[depth]}`) : '';
  const canGoDeeper  = depth < MAX_DEPTH;
  const newSugg      = (doc?.suggestions || []).filter(s => !ancestorSet.has(s.toLowerCase()));

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      <div style={{ ...s.termHeader, borderColor: dColor + '40', background: dColor + '10' }}>
        <div style={s.termEmoji}>{doc?.emoji || cat?.emoji || '💡'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...s.termName, color: dColor }}>{term}</div>
          {doc?.category && <div style={s.termCat}>{doc.category}</div>}
        </div>
        <div style={{ ...s.depthBadge, color: dColor, borderColor: dColor + '40', background: dColor + '15' }}>
          {t('knowledge.depthBadge', { label: dLabel, n: depth + 1 })}
        </div>
      </div>

      {depth > 2 && (
        <button style={{ ...s.backBtn, borderColor: dColor + '40', color: dColor }} onClick={onBack}>
          {t('knowledge.backToUpperLayer')}
        </button>
      )}

      {loading && (
        <div style={s.loadingBox}>
          <div style={s.spinner} />
          <span style={s.loadingText}>{t('knowledge.aiGenerating', { n: depth + 1 })}</span>
        </div>
      )}

      {doc && (
        <>
          <div style={s.defBox}>
            <div style={s.defLabel}>{t('knowledge.defLabel')}</div>
            <p style={s.defText}>{doc.definition}</p>
          </div>
          {doc.examples?.length > 0 && (
            <div style={s.defBox}>
              <div style={s.defLabel}>{t('knowledge.examplesLabel')}</div>
              {doc.examples.map((ex, i) => (
                <div key={i} style={s.exampleItem}><span style={{ color: dColor, marginRight: 6 }}>›</span>{ex}</div>
              ))}
            </div>
          )}
          {doc.related?.length > 0 && (
            <div style={s.defBox}>
              <div style={s.defLabel}>{t('knowledge.relatedLabel')}</div>
              <div style={s.relatedWrap}>
                {doc.related.map(rel => (
                  <button key={rel} onClick={() => onRelatedClick(rel)}
                    style={{ ...s.relatedChip, borderColor: dColor + '50' }}>{rel}</button>
                ))}
              </div>
            </div>
          )}
          {canGoDeeper && newSugg.length > 0 && (
            <div style={s.defBox}>
              <div style={s.defLabel}>{t('knowledge.exploreDeeperLabel', { n: depth + 2 })}</div>
              <div style={s.suggList}>
                {newSugg.map(sug => (
                  <button key={sug} onClick={() => onSuggestionDrill(sug)}
                    style={{ ...s.suggBtn, borderColor: dColor + '40' }}>
                    <span style={{ color: dColor, fontWeight: 700 }}>→</span>
                    <span>{sug}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {canGoDeeper && doc.suggestions?.length > 0 && newSugg.length === 0 && (
            <div style={s.maxDepthNote}>{t('knowledge.allExploredNote')}</div>
          )}
          {depth >= MAX_DEPTH && (
            <div style={s.maxDepthNote}>{t('knowledge.maxDepthNote', { n: depth + 1 })}</div>
          )}
        </>
      )}

      {!doc && !loading && (
        <div style={s.notLoadedBox}>
          <div style={s.notLoadedText}>{t('knowledge.notLoadedText')}</div>
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const s = {
  page:    { display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', background: 'var(--bg)' },
  mapWrap: { flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)', minWidth: 0 },
  mapHeader: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px',
    borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0, flexWrap: 'wrap',
  },
  mapTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' },
  mapHint:  { fontSize: 11, color: 'var(--text-dim)', flex: 1 },
  resetBtn: {
    padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border-mid)',
    background: 'var(--surface2)', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 4, flex: 1, flexWrap: 'wrap', overflow: 'hidden' },
  bcCat:  { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap', padding: '2px 6px', borderRadius: 6, background: 'var(--surface2)' },
  bcSep:  { fontSize: 11, color: 'var(--text-dim)', flexShrink: 0 },
  bcItem: { fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: '2px 4px' },
  svgScroll: { flex: 1, overflow: 'hidden', position: 'relative' },
  canvas:    { flex: 1, overflow: 'hidden', position: 'relative', cursor: 'grab' },
  svg:       { display: 'block', background: 'var(--bg)' },
  zoomControls: {
    position: 'absolute', top: 10, right: 10, zIndex: 10,
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '4px 8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  zoomBtn: {
    width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6,
    cursor: 'pointer', fontSize: 14, color: 'var(--text)', fontWeight: 700, lineHeight: 1,
  },
  zoomLabel: { fontSize: 11, color: 'var(--text-muted)', minWidth: 36, textAlign: 'center' },
  panel:     { width: 340, flexShrink: 0, overflowY: 'auto', padding: 20, background: 'var(--surface)' },

  emptyPanel: { display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40 },
  emptyIcon:  { fontSize: 48, marginBottom: 16 },
  emptyTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 },
  emptyDesc:  { fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.7, marginBottom: 24 },
  catGrid:    { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  catChip:    { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, border: '1px solid', background: 'var(--surface2)', fontSize: 12 },

  panelCatHeader:  { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 12, border: '1px solid', marginBottom: 16 },
  panelEmoji:      { fontSize: 28, flexShrink: 0, marginTop: 2 },
  panelCatName:    { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  panelCatDesc:    { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 },
  panelSection:    {},
  panelSectionTitle: { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: 0.8, marginBottom: 10 },
  termList:        { display: 'flex', flexDirection: 'column', gap: 4 },
  termBtn:         { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid', background: 'var(--surface2)', cursor: 'pointer', textAlign: 'left', width: '100%' },
  termBtnText:     { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },

  termHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 12, border: '1px solid', marginBottom: 12, flexWrap: 'wrap' },
  termEmoji:  { fontSize: 28, flexShrink: 0 },
  termName:   { fontSize: 16, fontWeight: 700, marginBottom: 2 },
  termCat:    { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' },
  depthBadge: { fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 10, border: '1px solid', letterSpacing: 0.3, whiteSpace: 'nowrap', alignSelf: 'flex-start' },
  backBtn:    { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid', background: 'transparent', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 14, width: '100%' },

  loadingBox:  { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0' },
  spinner:     { width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: 12, color: 'var(--text-muted)' },

  defBox:      { marginBottom: 16 },
  defLabel:    { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: 0.8, marginBottom: 8 },
  defText:     { fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 },
  exampleItem: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 6 },
  relatedWrap: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  relatedChip: { padding: '4px 10px', borderRadius: 12, border: '1px solid', background: 'var(--surface2)', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' },
  suggList:    { display: 'flex', flexDirection: 'column', gap: 6 },
  suggBtn:     { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid', background: 'var(--surface2)', color: 'var(--text)', fontSize: 12, cursor: 'pointer', textAlign: 'left', width: '100%' },
  maxDepthNote: { marginTop: 12, padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, fontSize: 12, color: '#92400e', lineHeight: 1.5 },
  notLoadedBox: { padding: '20px 0' },
  notLoadedText: { fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.7, textAlign: 'center' },
};
